import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CampaignAnalytics = ({ campaign, isOpen, onClose }) => {
  const [timeRange, setTimeRange] = useState('30d');

  const performanceData = [
    { name: 'Week 1', sent: 120, delivered: 118, opened: 89, clicked: 23, responded: 12 },
    { name: 'Week 2', sent: 135, delivered: 132, opened: 98, clicked: 31, responded: 18 },
    { name: 'Week 3', sent: 142, delivered: 140, opened: 105, clicked: 28, responded: 15 },
    { name: 'Week 4', sent: 128, delivered: 125, opened: 92, clicked: 25, responded: 14 }
  ];

  const engagementData = [
    { name: 'Jan', engagement: 65 },
    { name: 'Feb', engagement: 72 },
    { name: 'Mar', engagement: 68 },
    { name: 'Apr', engagement: 78 },
    { name: 'May', engagement: 82 },
    { name: 'Jun', engagement: 75 }
  ];

  const responseTypeData = [
    { name: 'Positive', value: 45, color: '#10B981' },
    { name: 'Neutral', value: 30, color: '#F59E0B' },
    { name: 'Negative', value: 15, color: '#DC2626' },
    { name: 'No Response', value: 10, color: '#6B7280' }
  ];

  const topPerformingMessages = [
    {
      id: 1,
      subject: "Happy Birthday! 🎉",
      openRate: 89.5,
      responseRate: 23.4,
      sentiment: "positive"
    },
    {
      id: 2,
      subject: "Your home anniversary is here!",
      openRate: 76.2,
      responseRate: 18.7,
      sentiment: "positive"
    },
    {
      id: 3,
      subject: "Market update for your neighborhood",
      openRate: 68.9,
      responseRate: 12.3,
      sentiment: "neutral"
    }
  ];

  const metrics = [
    {
      label: "Total Sent",
      value: "1,247",
      change: "+12.5%",
      trend: "up",
      icon: "Send"
    },
    {
      label: "Delivery Rate",
      value: "98.2%",
      change: "+0.8%",
      trend: "up",
      icon: "CheckCircle"
    },
    {
      label: "Open Rate",
      value: "74.6%",
      change: "+5.2%",
      trend: "up",
      icon: "Eye"
    },
    {
      label: "Response Rate",
      value: "19.3%",
      change: "-2.1%",
      trend: "down",
      icon: "MessageCircle"
    }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-7xl bg-card border border-border rounded-lg elevation-4 animate-scale-in max-h-[95vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Campaign Analytics</h2>
              <p className="text-sm text-muted-foreground">
                {campaign?.name || 'Campaign Performance'} • Last 30 days
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {metrics?.map((metric, index) => (
                <div key={index} className="bg-muted/20 border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name={metric?.icon} size={20} className="text-primary" />
                    </div>
                    <div className={`flex items-center space-x-1 text-sm ${
                      metric?.trend === 'up' ? 'text-success' : 'text-error'
                    }`}>
                      <Icon name={metric?.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} size={14} />
                      <span>{metric?.change}</span>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-foreground mb-1">{metric?.value}</div>
                  <div className="text-sm text-muted-foreground">{metric?.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Performance Chart */}
              <div className="bg-muted/20 border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Message Performance</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={performanceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis 
                        dataKey="name" 
                        stroke="var(--color-muted-foreground)"
                        fontSize={12}
                      />
                      <YAxis 
                        stroke="var(--color-muted-foreground)"
                        fontSize={12}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'var(--color-popover)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="sent" fill="var(--color-primary)" name="Sent" />
                      <Bar dataKey="delivered" fill="var(--color-success)" name="Delivered" />
                      <Bar dataKey="opened" fill="var(--color-accent)" name="Opened" />
                      <Bar dataKey="responded" fill="var(--color-warning)" name="Responded" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Engagement Trend */}
              <div className="bg-muted/20 border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Engagement Trend</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={engagementData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis 
                        dataKey="name" 
                        stroke="var(--color-muted-foreground)"
                        fontSize={12}
                      />
                      <YAxis 
                        stroke="var(--color-muted-foreground)"
                        fontSize={12}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'var(--color-popover)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '8px'
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="engagement" 
                        stroke="var(--color-primary)" 
                        strokeWidth={3}
                        dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Response Distribution */}
              <div className="bg-muted/20 border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Response Types</h3>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={responseTypeData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {responseTypeData?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry?.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'var(--color-popover)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '8px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2 mt-4">
                  {responseTypeData?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: item?.color }}
                        />
                        <span className="text-sm text-foreground">{item?.name}</span>
                      </div>
                      <span className="text-sm font-medium text-foreground">{item?.value}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Performing Messages */}
              <div className="lg:col-span-2 bg-muted/20 border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Top Performing Messages</h3>
                <div className="space-y-4">
                  {topPerformingMessages?.map((message, index) => (
                    <div key={message?.id} className="flex items-center justify-between p-4 bg-card border border-border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-foreground">#{index + 1}</span>
                          <h4 className="font-medium text-foreground">{message?.subject}</h4>
                          <div className={`w-2 h-2 rounded-full ${
                            message?.sentiment === 'positive' ? 'bg-success' : 
                            message?.sentiment === 'neutral' ? 'bg-warning' : 'bg-error'
                          }`} />
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                          <span>Open Rate: {message?.openRate}%</span>
                          <span>Response Rate: {message?.responseRate}%</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon">
                        <Icon name="MoreVertical" size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-border p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Data updated 5 minutes ago
              </div>
              <div className="flex items-center space-x-3">
                <Button variant="outline">
                  <Icon name="Download" size={16} />
                  Export Report
                </Button>
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignAnalytics;